// Location + Service combo pages (e.g. "Roof Repair in Vienna VA").
// Generates high-value, long-tail local SEO landing pages that pair each
// core service with each service area.
import { serviceContent } from "./serviceContent";
import { serviceAreas, getServiceArea } from "./mock";
import { placeName, LANDING_BY_CITY } from "./seo";

// Core services that get a dedicated page in every city.
export const comboServiceSlugs = [
  "roof-repair",
  "roof-replacement",
  "roofing",
  "storm-damage-repairs",
  "siding",
  "windows",
  "gutters",
];

// All valid { serviceSlug, citySlug } combinations.
export const comboPaths = comboServiceSlugs.flatMap((serviceSlug) =>
  serviceAreas.map((area) => ({ serviceSlug, citySlug: area.slug }))
);

export const getCombo = (serviceSlug, citySlug) => {
  if (!comboServiceSlugs.includes(serviceSlug)) return null;
  const svc = serviceContent[serviceSlug];
  const area = getServiceArea(citySlug);
  if (!svc || !area) return null;

  const zips = area.zips.join(", ");
  const place = placeName(area);
  const metaTitle = `${svc.name} in ${place} | Fab Build Pro`;
  const metaDesc = `${svc.name} in ${place} by Fab Build Pro, a licensed & insured GAF Master Elite contractor. Free estimates. Call 703-766-6599.`;
  const h1 = `${svc.name} in ${place}`;
  // Consolidate overlapping pages: roofing combos -> city/landing page; windows combos are not indexed.
  const landing = LANDING_BY_CITY[citySlug];
  let canonicalPath;
  if (serviceSlug === "roofing") canonicalPath = landing || `/service-areas/${citySlug}`;
  if (serviceSlug === "roof-repair" && landing) canonicalPath = landing;
  const noindex = serviceSlug === "windows";

  const intro = [
    `When ${area.name} homeowners need ${svc.name.toLowerCase()}, they call Fab Build Pro. We're a licensed, insured, GAF Master Elite contractor serving ${area.name} and the surrounding ${area.region} — including ${zips}. Our local crews know the ${area.region} climate and deliver durable, professional results backed by our 100% satisfaction guarantee.`,
    svc.intro[0],
    `From your first free inspection to the final clean-up, we make ${svc.name.toLowerCase()} in ${area.name} straightforward and stress-free — with honest, upfront pricing and flexible financing options. Many storm-related projects qualify for insurance coverage with little out-of-pocket cost.`,
  ];

  const faqs = [
    {
      q: `Do you provide ${svc.name.toLowerCase()} in ${area.name}, VA?`,
      a: `Yes. Fab Build Pro provides ${svc.name.toLowerCase()} throughout ${area.name} (${zips}) and the wider ${area.region}. Call 703-766-6599 for a free, no-obligation estimate.`,
    },
    {
      q: `How fast can you start a ${svc.name.toLowerCase()} project in ${area.name}?`,
      a: `We can typically inspect and provide a written estimate within 24–48 hours in ${area.name}, and we offer 24/7 storm response for urgent situations.`,
    },
    ...svc.faqs.slice(0, 2),
    {
      q: `Is Fab Build Pro licensed and insured to work in ${area.name}?`,
      a: `Yes — we are fully licensed and insured and are a GAF Master Elite certified contractor serving ${area.region}.`,
    },
  ];

  const nearbyCities = serviceAreas
    .filter((a) => a.slug !== area.slug)
    .slice(0, 8);

  const otherServices = comboServiceSlugs
    .filter((s) => s !== serviceSlug)
    .map((s) => ({ slug: s, name: serviceContent[s].name }));

  return {
    svc,
    area,
    serviceSlug,
    citySlug,
    zips,
    place,
    canonicalPath,
    noindex,
    metaTitle,
    metaDesc,
    h1,
    intro,
    faqs,
    nearbyCities,
    otherServices,
  };
};
